/* eslint-disable */
/* ============================================================
   boot-screen.jsx — kevinz.ai
   ------------------------------------------------------------
   TUI-style POST sequence overlay. Plays once per session, fades
   into the page. Skipped on subsequent visits via sessionStorage,
   on ?skip-boot, and when prefers-reduced-motion is set.

   See: HANDOFF-V3.md §1, Linear CC-484 (A1).
   Render BEFORE <TopBar /> in app.jsx.
   ============================================================ */

import React, { useEffect as _bsUseEffect, useState as _bsUseState, useRef as _bsUseRef } from 'react';

// kind: "ok" = green ✓, "progress" = amber → in-progress, "dim" = grey meta, "bar" = progress bar, "header" = top border, "prompt" = final prompt
const BOOT_LINES = [
  { t: 0,    line: null,                                                                kind: "header" },
  { t: 80,   line: "✓ Boot kernel",                                                    kind: "ok" },
  { t: 180,  line: "✓ Loading OpenClaw runtime…",                                      kind: "ok" },
  { t: 300,  line: "✓ Loading Claude Code CLI v2.1.119",                               kind: "ok" },
  { t: 420,  line: "✓ Loading Codex CLI",                                              kind: "ok" },
  { t: 540,  line: "✓ Loading CC Commander v6.6.0",                            kind: "ok" },
  { t: 660,  line: "✓ Loading gstack",                                                 kind: "ok" },
  { t: 760,  line: "✓ Loading superpowers",                                            kind: "ok" },
  { t: 860,  line: "✓ Loading Compound Engineering",                                   kind: "ok" },
  { t: 960,  line: "✓ Loading repomix",                                                kind: "ok" },
  { t: 1060, line: "✓ Loading ECC",                                             kind: "ok" },
  { t: 1180, line: "✓ Mounting agent fleet (online)",                        kind: "ok" },
  { t: 1320, line: "✓ Loading 72 plugin skills",                                            kind: "ok" },
  { t: 1460, line: "✓ Connecting to Anthropic, OpenAI, Google, xAI, Meta",            kind: "ok" },
  { t: 1620, line: "✓ Inference: NVIDIA Inception stack",                              kind: "ok" },
  { t: 1760, line: "✓ ID 0001 — Kevin Zicherman",                                     kind: "ok" },
  { t: 1900, line: "✓ Ready. Initializing kevinz.ai…",                                kind: "ok" },
  { t: 2100, line: null,                                                                kind: "footer" },
  { t: 2350, line: "> kevinz@toronto:~ $ welcome.",                                   kind: "prompt" },
];

const TOTAL_LINES = BOOT_LINES.length;
const BOOT_DURATION = 2700;
const SESSION_KEY = "kz_booted";

// Build the header bar fitting a given width (in chars)
function makeHeaderBar(width) {
  const label = " KEVINZ.AI BOOT v4.0 ";
  const fill = Math.max(0, width - label.length - 2); // 2 for corners
  const left = Math.floor(fill / 2);
  const right = fill - left;
  return "┌" + "─".repeat(left) + label + "─".repeat(right) + "┐";
}

function makeFooterBar(width) {
  return "└" + "─".repeat(width - 2) + "┘";
}

function makeProgressBar(fraction, width) {
  // [ ████████░░░░ ] 80% · loading 12/17 modules
  const innerWidth = width - 4; // account for [ and ] and spaces
  const filled = Math.round(fraction * innerWidth);
  const empty = innerWidth - filled;
  const pct = Math.round(fraction * 100);
  const bar = "[ " + "█".repeat(filled) + "░".repeat(empty) + " ]";
  return bar + " " + pct + "%";
}

function BootScreen() {
  const [done, setDone] = _bsUseState(() => {
    if (typeof window === "undefined") return true;
    // Opt-in by default: only play boot when explicitly requested via
    // `?theatre=1` (devs sharing the URL). Non-dev visitors get instant hero.
    // Was: opt-out via ?skip-boot + sessionStorage. See CC-870 (design polish).
    const params = new URLSearchParams(window.location.search);
    if (!params.has("theatre")) return true;
    if (params.has("skip-boot")) return true;
    if (sessionStorage.getItem(SESSION_KEY) === "1") return true;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return true;
    return false;
  });
  const [visibleCount, setVisibleCount] = _bsUseState(0);
  const [fading, setFading] = _bsUseState(false);
  const timeoutsRef = _bsUseRef([]);

  _bsUseEffect(() => {
    if (done) return;
    BOOT_LINES.forEach((entry, i) => {
      timeoutsRef.current.push(setTimeout(() => setVisibleCount(i + 1), entry.t));
    });
    timeoutsRef.current.push(setTimeout(() => setFading(true), BOOT_DURATION - 250));
    timeoutsRef.current.push(setTimeout(() => {
      sessionStorage.setItem(SESSION_KEY, "1");
      setDone(true);
    }, BOOT_DURATION + 200));
    return () => timeoutsRef.current.forEach(clearTimeout);
  }, [done]);

  _bsUseEffect(() => {
    if (done) return;
    const dismiss = () => {
      timeoutsRef.current.forEach(clearTimeout);
      sessionStorage.setItem(SESSION_KEY, "1");
      setFading(true);
      setTimeout(() => setDone(true), 220);
    };
    window.addEventListener("keydown", dismiss);
    window.addEventListener("pointerdown", dismiss);
    return () => {
      window.removeEventListener("keydown", dismiss);
      window.removeEventListener("pointerdown", dismiss);
    };
  }, [done]);

  if (done) return null;

  // Count "content" lines (non-header/footer/bar) for progress
  const contentLines = BOOT_LINES.filter(e => e.kind !== "header" && e.kind !== "footer");
  const visibleContentCount = BOOT_LINES.slice(0, visibleCount).filter(e => e.kind !== "header" && e.kind !== "footer").length;
  const progressFraction = contentLines.length > 0 ? visibleContentCount / contentLines.length : 0;

  // TUI bar width — chars, fits ~72ch which looks good at most viewports
  const BAR_WIDTH = 60;

  return (
    <div
      role="status"
      aria-label="kevinz.ai boot sequence"
      className="kz-boot-outer"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "#000", color: "#e8e6dc",
        fontFamily: "var(--mono, 'JetBrains Mono', monospace)",
        fontSize: 13, lineHeight: 1.6,
        opacity: fading ? 0 : 1,
        transition: "opacity 220ms ease-out",
        overflow: "hidden", pointerEvents: fading ? "none" : "auto",
        display: "flex", flexDirection: "column", justifyContent: "flex-start",
        padding: "44px 48px",
      }}>
      <div className="kz-boot-inner" style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {BOOT_LINES.slice(0, visibleCount).map((entry, i) => {
          if (entry.kind === "header") {
            return (
              <div key={i} style={{ color: "#5b5848", whiteSpace: "pre", marginBottom: 6, fontFamily: "inherit" }}>
                {makeHeaderBar(BAR_WIDTH)}
              </div>
            );
          }
          if (entry.kind === "footer") {
            return (
              <div key={i} style={{ color: "#5b5848", whiteSpace: "pre", marginTop: 6, fontFamily: "inherit" }}>
                {makeFooterBar(BAR_WIDTH)}
              </div>
            );
          }
          if (entry.kind === "ok") {
            // ✓ in green, rest in warm white
            const checkmark = entry.line.startsWith("✓") ? entry.line.slice(0, 1) : "";
            const rest = entry.line.startsWith("✓") ? entry.line.slice(1) : entry.line;
            return (
              <div key={i} style={{ whiteSpace: "pre", fontFamily: "inherit", display: "flex" }}>
                {checkmark && <span style={{ color: "#3fb950" }}>{checkmark}</span>}
                <span style={{ color: "#e8e6dc" }}>{rest}</span>
              </div>
            );
          }
          if (entry.kind === "prompt") {
            return (
              <div key={i} style={{ color: "#0096FF", whiteSpace: "pre", fontFamily: "inherit", marginTop: 10 }}>
                {entry.line}
                <span style={{ animation: "kz-blink 1s step-end infinite" }}>_</span>
              </div>
            );
          }
          if (entry.kind === "dim") {
            return (
              <div key={i} style={{ color: "#5b5848", whiteSpace: "pre", fontFamily: "inherit" }}>
                {entry.line}
              </div>
            );
          }
          return (
            <div key={i} style={{ color: "#e8e6dc", whiteSpace: "pre", fontFamily: "inherit" }}>
              {entry.line}
            </div>
          );
        })}

        {/* Cursor blink on current line while loading */}
        {visibleCount > 0 && visibleCount < BOOT_LINES.length && (
          <div style={{ color: "#e8e6dc", marginTop: 2, display: "flex", alignItems: "center" }}>
            <span style={{ color: "#5b5848", marginRight: 6 }}>→</span>
            <span style={{ color: "#f0a500", animation: "kz-blink 0.8s step-end infinite" }}>█</span>
          </div>
        )}

        {/* Progress bar — always show once boot starts */}
        {visibleCount > 0 && (
          <div style={{ color: "#5b5848", whiteSpace: "pre", fontFamily: "inherit", marginTop: 10 }}>
            {makeProgressBar(progressFraction, BAR_WIDTH)}
          </div>
        )}
      </div>
      <style>{`
        @keyframes kz-blink { 50% { opacity: 0 } }
        .kz-boot-outer { padding: 44px 48px; }
        @media (max-width: 768px) {
          .kz-boot-outer { padding: 24px 10px; }
          .kz-boot-inner { width: 100%; }
        }
        @media (min-width: 769px) and (max-width: 1100px) {
          .kz-boot-outer { padding: 44px 24px; }
        }
      `}</style>
    </div>
  );
}

Object.assign(window, { BootScreen });

export { BootScreen };
